import React, { useState } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL = '#0d9488'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function AddPatient({ onSuccess, clinicId }) {
  const [form, setForm] = useState({ name: '', patient_email: '', injury_type: '', injury_type_other: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSubmit = () => {
    setError('')
    if (!form.name || !form.injury_type) { setError('Please fill in name and injury type.'); return }
    if (form.injury_type === 'Other' && !form.injury_type_other) { setError('Please specify the injury type.'); return }
    const finalInjuryType = form.injury_type === 'Other' ? form.injury_type_other : form.injury_type
    setLoading(true)
    axios.post(API + '/patients', {
  name: form.name,
  patient_email: form.patient_email || null,
  injury_type: finalInjuryType,
  clinic_id: clinicId,
})
      .then(res => onSuccess(res.data.email_sent, form.patient_email))
      .catch(err => { setError('Something went wrong. Please try again.'); console.error(err) })
      .finally(() => setLoading(false))
  }

  return (
    <div style={{ maxWidth: '520px', fontFamily: FONT }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Add New Patient</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px', lineHeight: '1.5' }}>
          Enter the patient's name, email, and injury. They'll receive an email to complete their intake form and create their portal account.
        </p>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.78)', borderRadius: '14px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={lS}>Full Name *</label>
            <input name="name" placeholder="e.g. Margaret Smith" onChange={handleChange} value={form.name} style={iS} />
          </div>
          <div>
            <label style={lS}>Email Address</label>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 5px' }}>Used to send the intake form link. Recommended but optional.</p>
            <input name="patient_email" placeholder="e.g. margaret@email.com" type="email" onChange={handleChange} value={form.patient_email} style={iS} />
          </div>
          <div>
            <label style={lS}>Injury Type *</label>
            <select name="injury_type" onChange={handleChange} value={form.injury_type} style={{ ...iS, color: form.injury_type ? '#1e293b' : '#94a3b8' }}>
              <option value="">Select injury type</option>
              <optgroup label="Spine">
                <option>Lower Back Pain</option>
                <option>Herniated Disc</option>
                <option>Sciatica</option>
                <option>Neck Pain / Cervical Strain</option>
                <option>Scoliosis</option>
                <option>Spinal Stenosis</option>
                <option>Post-Spinal Surgery</option>
              </optgroup>
              <optgroup label="Knee">
                <option>ACL Tear</option>
                <option>Meniscus Tear</option>
                <option>Patellofemoral Syndrome</option>
                <option>Knee Replacement</option>
                <option>Patellar Tendinitis</option>
                <option>IT Band Syndrome</option>
              </optgroup>
              <optgroup label="Shoulder">
                <option>Rotator Cuff Tear</option>
                <option>Shoulder Impingement</option>
                <option>Frozen Shoulder</option>
                <option>Shoulder Replacement</option>
                <option>Labrum Tear</option>
                <option>Shoulder Dislocation</option>
              </optgroup>
              <optgroup label="Hip">
                <option>Hip Replacement</option>
                <option>Hip Flexor Strain</option>
                <option>Hip Labrum Tear</option>
                <option>Hip Bursitis</option>
                <option>Piriformis Syndrome</option>
              </optgroup>
              <optgroup label="Ankle & Foot">
                <option>Ankle Sprain</option>
                <option>Plantar Fasciitis</option>
                <option>Achilles Tendinitis</option>
                <option>Stress Fracture</option>
              </optgroup>
              <optgroup label="Elbow & Wrist">
                <option>Tennis Elbow</option>
                <option>Golfer's Elbow</option>
                <option>Carpal Tunnel Syndrome</option>
                <option>Wrist Fracture</option>
              </optgroup>
              <optgroup label="Post-Surgical">
                <option>Post-ACL Reconstruction</option>
                <option>Post-Knee Replacement</option>
                <option>Post-Hip Replacement</option>
                <option>Post-Shoulder Surgery</option>
                <option>Post-Spinal Fusion</option>
              </optgroup>
              <optgroup label="Other Conditions">
                <option>Osteoarthritis</option>
                <option>Osteoporosis</option>
                <option>Fibromyalgia</option>
                <option>Vertigo / BPPV</option>
                <option>Balance Disorder</option>
                <option>Concussion</option>
                <option>Stroke Rehabilitation</option>
                <option>Parkinson's Disease</option>
                <option>Multiple Sclerosis</option>
                <option value="Other">Other (specify below)</option>
              </optgroup>
            </select>
          </div>
          {form.injury_type === 'Other' && (
            <div>
              <label style={lS}>Please specify injury</label>
              <input name="injury_type_other" placeholder="Describe the injury or condition" onChange={handleChange} value={form.injury_type_other} style={iS} />
            </div>
          )}
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}
          <button onClick={handleSubmit} disabled={loading} style={{ padding: '12px', backgroundColor: loading ? '#94a3b8' : TEAL, color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'default' : 'pointer', fontSize: '15px', fontWeight: '600', fontFamily: FONT, marginTop: '4px' }}>
            {loading ? 'Adding...' : form.patient_email ? ' Add Patient & Send Intake Form' : ' Add Patient'}
          </button>
          {form.patient_email && (
            <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', margin: 0 }}>
               An intake form link will be emailed to <strong>{form.patient_email}</strong>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

const lS = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' }
const iS = { width: '100%', padding: '10px 12px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '8px', boxSizing: 'border-box', color: '#1e293b', background: 'white', fontFamily: FONT, outline: 'none' }

export default AddPatient